import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import Swal from "sweetalert2";
import { uploadMultipleAssets, uploadSingleAsset } from "../api/uploadAssets";
import { useAdminSession } from "../context/AdminSessionContext";
import { hasRoleAtLeast } from "../utils/roleHelpers";
import { hasPermission } from "../utils/permissionHelpers";
import { approveContentSubmission, rejectContentSubmission } from "../api/adminContentApi";
import { getUsersPaginated, transferContentOwnership } from "../api/adminUsersApi";
import { getContentPartRevenue, type ContentPartRevenueResponse } from "../api/adminReportsApi";
import { getSingleContentPerformanceStats, type ContentPerformanceSeries } from "../api/adminStatsApi";
import { assetBaseUrl } from "../config/env";

interface ContentItem {
  id: string;
  title: string;
  type: string;
  author: string;
  actor?: string | null;
  category: string;
  description?: string | null;
  heroImageUrl?: string | null;
  coverImageUrl?: string | null;
  tileImageUrl?: string | null;
  releaseDate?: string | null;
  createdByUserId?: string | null;
  isMemberSubmission?: boolean;
  isApproved?: boolean;
  viewCount?: number;
  totalQuotaConsumptionCount?: number;
  uniqueReaderCount?: number;
  favoriteCount?: number;
  commentCount?: number;
  reactionCount?: number;
  latestPartOrder?: number | null;
  latestPartTitle?: string | null;
  latestPartCreatedAtUtc?: string | null;
  pendingPartCount?: number;
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(value ?? 0);
}

function StatChip({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2 py-2">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function RatioRow({ label, value, total }: { label: string; value: number; total: number }) {const ratio = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-slate-700">
        <span>{label}</span>
        <span>
          {value} ({ratio}%)
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded bg-slate-200">
        <div className="h-full rounded bg-indigo-500" style={{ width: `${Math.max(2, ratio)}%` }} />
      </div>
    </div>
  );
}

function MiniReadChart({ points }: { points: Array<{ date: string; readCount: number }> }) {
  const max = Math.max(...points.map((point) => point.readCount), 1);
  return (
    <div>
      <div className="flex h-28 items-end gap-1">
        {points.map((point) => {
          const ratio = Math.max(8, Math.round((point.readCount / max) * 100));
          return (
            <div key={`${point.date}-${point.readCount}`} className="group flex min-w-0 flex-1 items-end">
              <div className="relative h-full w-full rounded-t bg-indigo-500/85">
                <div className="absolute bottom-0 w-full rounded-t bg-indigo-500" style={{ height: `${ratio}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2 text-[10px] text-slate-500">
        {points.slice(-4).map((point) => (
          <div key={`label-${point.date}`} className="truncate">
            {point.date}
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueBar({
  label,
  value,
  max,
}: {
  label: string;
  value: number;
  max: number;
}) {const percent = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="truncate text-slate-700">{label}</span>
        <span className="font-semibold text-slate-800">{formatCompact(value)} Z</span>
      </div>
      <div className="h-2 overflow-hidden rounded bg-slate-200">
        <div className="h-full rounded bg-emerald-500" style={{ width: `${Math.max(4, percent)}%` }} />
      </div>
    </div>
  );
}

interface ContentPartItem {
  id: string;
  title: string;
  description?: string | null;
  order?: number | null;
  price?: number | null;
  isFreePreview?: boolean;
  mediaBanner?: string | null;
  mediaUrl?: string | null;
  imageList?: string[] | null;
  viewCount?: number;
}

interface OwnerOption {
  id: string;
  name: string;
  email: string;
  role: string;
  roles: string[];
}

function isCreatorRoleLabel(role?: string | null): boolean {
  const normalized = String(role ?? "").trim().toLocaleLowerCase("tr-TR");
  if (!normalized) {
    return false;
  }

  return normalized.includes("creator") || normalized.includes("cizer") || normalized.includes("\u00E7izer");
}

function ownerHasCreatorRole(owner: OwnerOption): boolean {
  return [owner.role, ...owner.roles].some((role) => isCreatorRoleLabel(role));
}

function isCreatorOwnedContent(content: ContentItem, ownerMap: Map<string, OwnerOption>): boolean {
  if (content.isMemberSubmission) {
    return true;
  }

  if (!content.createdByUserId) {
    return false;
  }

  const owner = ownerMap.get(content.createdByUserId);return owner ? ownerHasCreatorRole(owner) : false;
}

type UploadKey = "heroImageUrl" | "coverImageUrl" | "tileImageUrl";
type PartUploadKey = "mediaBanner" | "mediaUrl";
type ContentStatusFilter = "all" | "approved" | "pending";
type ContentSortMode = "recent" | "views" | "favorites" | "quota";
type ContentDetailTab = "overview" | "edit" | "parts";

const CONTENT_TYPES = ["Webtoon", "Manga", "Novel"];
const uploadsBase = String(assetBaseUrl ?? "").replace(/\/$/, "");

const defaultContentForm = {
  id: "",
  title: "",
  type: "Webtoon",
  author: "",
  actor: "",
  category: "",
  description: "",
  releaseDate: new Date().toISOString().slice(0, 10),
  heroImageUrl: "",
  coverImageUrl: "",
  tileImageUrl: "",
};

const defaultPartForm = {
  id: "",
  contentId: "",
  title: "",
  description: "",
  order: 1,
  price: "",
  isFreePreview: true,
  mediaBanner: "",
  mediaUrl: "",
  imageList: [] as string[],
};

const resolveAssetUrl = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const trimmed = value.trim();
  const noLeadingSlash = trimmed.replace(/^\/+/, "");

  if (noLeadingSlash.startsWith("uploads/")) {
    return uploadsBase ? `${uploadsBase}/${noLeadingSlash}` : `/${noLeadingSlash}`;
  }

  if (trimmed.startsWith("/")) {
    return uploadsBase ? `${uploadsBase}${trimmed}` : trimmed;
  }

  return uploadsBase ? `${uploadsBase}/${noLeadingSlash}` : `/${noLeadingSlash}`;
};

export default function ContentManagementPanel() {
  const { session } = useAdminSession();
  const permissions = session?.permissions;
  const creatorScopeId = useMemo(() => {
    if (!session || !session.roles?.length) {
      return undefined;
    }
    const roles = session.roles;
    const isCreator = hasRoleAtLeast(roles, "creator");
    const isModeratorOrAbove = hasRoleAtLeast(roles, "moderator");
    if (isCreator && !isModeratorOrAbove) {
      return session.id;
    }
    return undefined;
  }, [session]);
  const isAdminUser = useMemo(
    () => (session?.roles ?? []).some((role) => role.toLowerCase() === "admin"),
    [session?.roles],
  );
  const canCreateContent = hasPermission(permissions, "Content.Create");
  const canEditAllContent = hasPermission(permissions, "Content.EditAll");
  const canEditOwnContent = hasPermission(permissions, "Content.EditOwn");
  const canDeleteContent = hasPermission(permissions, "Content.Delete");
  const canTransferOwnership = hasPermission(permissions, "Users.AssignRole");
  const canPublishContent = hasPermission(permissions, "Content.Publish");

  const [contents, setContents] = useState<ContentItem[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<ContentStatusFilter>("all");
  const [sortMode, setSortMode] = useState<ContentSortMode>("recent");
  const [creatorFilterUserId, setCreatorFilterUserId] = useState("");
  const [contentForm, setContentForm] = useState(defaultContentForm);
  const [selectedContentId, setSelectedContentId] = useState<string>("");
  const [detailTab, setDetailTab] = useState<ContentDetailTab>("overview");
  const [showDetailView, setShowDetailView] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [partRevenueStats, setPartRevenueStats] = useState<ContentPartRevenueResponse | null>(null);
  const [performanceSeries, setPerformanceSeries] = useState<ContentPerformanceSeries | null>(null);
  const [uploadingContent, setUploadingContent] = useState<Record<UploadKey, boolean>>({
    heroImageUrl: false,
    coverImageUrl: false,
    tileImageUrl: false,
  });
  const [savingContent, setSavingContent] = useState(false);

  const [parts, setParts] = useState<ContentPartItem[]>([]);
  const [partForm, setPartForm] = useState(defaultPartForm);
  const [partLoading, setPartLoading] = useState(false);
  const [uploadingPart, setUploadingPart] = useState({ banner: false, doc: false, images: false });
  const [savingPart, setSavingPart] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState<OwnerOption[]>([]);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const [ownerSearchResults, setOwnerSearchResults] = useState<OwnerOption[]>([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [ownershipTargetUserId, setOwnershipTargetUserId] = useState("");
  const [ownershipNote, setOwnershipNote] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState<string[]>([]);
  const [bulkModerationLoading, setBulkModerationLoading] = useState(false);
  const selectedContent = useMemo(() => contents.find((content) => content.id === selectedContentId) ?? null, [contents, selectedContentId]);
  const canEditSelectedContent = useMemo(() => {
    if (!selectedContent) {
      return canEditAllContent || canEditOwnContent;
    }
    if (canEditAllContent) {
      return true;
    }
    if (canEditOwnContent && selectedContent.createdByUserId && session?.id) {
      return selectedContent.createdByUserId === session.id;
    }
    return false;
  }, [canEditAllContent, canEditOwnContent, selectedContent, session?.id]);
  const creatorRestricted = Boolean(creatorScopeId);
  const selectedContentOwned =
    !creatorRestricted || (selectedContent?.createdByUserId ?? null) === creatorScopeId;
  const ownerMap = useMemo(() => {
    const map = new Map<string, OwnerOption>();
    ownerOptions.forEach((owner) => {
      map.set(owner.id, owner);
    });
    return map;
  }, [ownerOptions]);
  const creatorOwnerOptions = useMemo(() => ownerOptions.filter((owner) => ownerHasCreatorRole(owner)), [ownerOptions]);
  const transferOwnerOptions = useMemo(() => {
    const normalized = ownerSearchTerm.trim();
    const source = normalized.length >= 2 ? ownerSearchResults : ownerOptions;
    if (!ownershipTargetUserId || source.some((owner) => owner.id === ownershipTargetUserId)) {
      return source;
    }
    const selectedOwner = ownerOptions.find((owner) => owner.id === ownershipTargetUserId);
    return selectedOwner ? [selectedOwner, ...source] : source;
  }, [ownerOptions, ownerSearchResults, ownerSearchTerm, ownershipTargetUserId]);

  useEffect(() => {
    if (!isAdminUser || creatorRestricted) {
      setOwnerOptions([]);
      setOwnerSearchResults([]);
      return;
    }

    let active = true;
    const loadOwners = async () => {
      const pageSize = 500;
      let skip = 0;
      let totalCount = Number.MAX_SAFE_INTEGER;
      const allUsers: OwnerOption[] = [];

      while (active && skip < totalCount && skip < 5000) {
        const response = await getUsersPaginated({
          take: pageSize,
          skip,
          sort: "nameAsc",
        });

        if (!active) {
          return;
        }

        const users: OwnerOption[] = response.items.map((user) => ({
          id: user.id,
          name: user.username || "Bilinmeyen kullanıcı",
          email: user.email || "",
          role: user.role || "",
          roles: Array.isArray(user.roles) ? user.roles : user.role ? [user.role] : [],
        }));

        if (users.length === 0) {
          break;
        }

        allUsers.push(...users);
        totalCount = Number.isFinite(response.totalCount) ? Math.max(response.totalCount, users.length) : totalCount;
        skip += users.length;
        if (users.length < pageSize) {
          break;
        }
      }

      const deduped = Array.from(
        allUsers.reduce((map, owner) => map.set(owner.id, owner), new Map<string, OwnerOption>()).values(),
      );
      setOwnerOptions(deduped);
      setOwnerSearchResults(deduped);
    };

    void loadOwners();
    return () => {
      active = false;
    };
  }, [isAdminUser, creatorRestricted]);

  useEffect(() => {
    if (!isAdminUser || creatorRestricted) {
      return;
    }

    const normalized = ownerSearchTerm.trim();
    if (normalized.length < 2) {
      setOwnerSearchResults(ownerOptions);
      return;
    }

    let active = true;
    setOwnerSearchLoading(true);
    const timer = window.setTimeout(async () => {
      const response = await getUsersPaginated({
        search: normalized,
        take: 80,
        skip: 0,
        sort: "nameAsc",
      });

      if (!active) {
        return;
      }

      const users: OwnerOption[] = response.items.map((user) => ({
        id: user.id,
        name: user.username || "Bilinmeyen kullanıcı",
        email: user.email || "",
        role: user.role || "",
        roles: Array.isArray(user.roles) ? user.roles : user.role ? [user.role] : [],
      }));
      setOwnerSearchResults(users);
      setOwnerSearchLoading(false);
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
      setOwnerSearchLoading(false);
    };
  }, [creatorRestricted, isAdminUser, ownerOptions, ownerSearchTerm]);

  useEffect(() => {
    if (!selectedContent) {
      setOwnershipTargetUserId("");
      return;
    }
    setOwnershipTargetUserId(selectedContent.createdByUserId ?? "");
  }, [selectedContent]);

  useEffect(() => {
    if (selectedContentId) {
      void loadParts(selectedContentId);
    } else {
      setParts([]);
    }
  }, [selectedContentId]);

  const filteredContents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = contents.filter((content) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        content.title?.toLowerCase().includes(normalizedSearch) ||
        content.author?.toLowerCase().includes(normalizedSearch) ||
        content.category?.toLowerCase().includes(normalizedSearch);

      const matchesType = typeFilter === "all" || content.type === typeFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "approved" ? Boolean(content.isApproved) : !Boolean(content.isApproved));

      return matchesSearch && matchesType && matchesStatus;
    });

    const sorted = [...filtered];
    sorted.sort((left, right) => {
      if (sortMode === "views") {
        return (right.viewCount ?? 0) - (left.viewCount ?? 0);
      }
      if (sortMode === "favorites") {
        return (right.favoriteCount ?? 0) - (left.favoriteCount ?? 0);
      }
      if (sortMode === "quota") {
        return (right.totalQuotaConsumptionCount ?? 0) - (left.totalQuotaConsumptionCount ?? 0);
      }

      const rightDate = right.latestPartCreatedAtUtc ?? right.releaseDate ?? "";
      const leftDate = left.latestPartCreatedAtUtc ?? left.releaseDate ?? "";
      return String(rightDate).localeCompare(String(leftDate));
    });
    return sorted;
  }, [contents, searchTerm, typeFilter, statusFilter, sortMode]);

  const catalogSummary = useMemo(() => {
    const total = filteredContents.length;
    const approved = filteredContents.filter((item) => Boolean(item.isApproved)).length;
    const pending = total - approved;
    const creatorOwned = filteredContents.filter((item) => isCreatorOwnedContent(item, ownerMap)).length;
    const platformOwned = Math.max(0, total - creatorOwned);
    const pendingParts = filteredContents.reduce((sum, item) => sum + Number(item.pendingPartCount ?? 0), 0);
    return { total, approved, pending, creatorOwned, platformOwned, pendingParts };
  }, [filteredContents, ownerMap]);

  const moderationQueues = useMemo(() => {
    const pendingContents = filteredContents
      .filter((item) => !item.isApproved)
      .sort((a, b) => (b.latestPartCreatedAtUtc ?? "").localeCompare(a.latestPartCreatedAtUtc ?? ""))
      .slice(0, 6);
    const pendingPartsContents = filteredContents
      .filter((item) => Number(item.pendingPartCount ?? 0) > 0)
      .sort((a, b) => Number(b.pendingPartCount ?? 0) - Number(a.pendingPartCount ?? 0))
      .slice(0, 6);
    return { pendingContents, pendingPartsContents };
  }, [filteredContents]);

  useEffect(() => {
    setSelectedQueueIds((prev) => prev.filter((id) => moderationQueues.pendingContents.some((item) => item.id === id)));
  }, [moderationQueues.pendingContents]);

  const loadContents = useCallback(async () => {
    setContentLoading(true);
    try {
      const params: Record<string, string | number> = { page: 1, pageSize: 200 };
      if (creatorScopeId) {
        params.createdByUserId = creatorScopeId;
      } else if (isAdminUser && creatorFilterUserId) {
        params.createdByUserId = creatorFilterUserId;
      }
      const response = await axios.get("/content", { params });
      const rawItems: any[] = Array.isArray(response.data) ? response.data : [];
      const items: ContentItem[] = rawItems.map((item) => ({
        id: String(item.id ?? item.Id ?? ""),
        title: item.title ?? item.Title ?? "",
        type: item.type ?? item.Type ?? "Webtoon",
        author: item.author ?? item.Author ?? "",
        actor: item.actor ?? item.Actor ?? "",
        category: item.category ?? item.Category ?? "",
        description: item.description ?? item.Description ?? "",
        heroImageUrl: item.heroImageUrl ?? item.HeroImageUrl ?? item.bannerUrl ?? item.BannerUrl ?? "",
        coverImageUrl: item.coverImageUrl ?? item.CoverImageUrl ?? item.img1 ?? item.Img1 ?? "",
        tileImageUrl: item.tileImageUrl ?? item.TileImageUrl ?? item.img2 ?? item.Img2 ?? "",
        releaseDate: item.releaseDate ?? item.ReleaseDate ?? null,
        createdByUserId: item.createdByUserId ?? item.CreatedByUserId ?? undefined,
        isMemberSubmission: Boolean(item.isMemberSubmission ?? item.IsMemberSubmission ?? false),
        isApproved: Boolean(item.isApproved ?? item.IsApproved ?? false),
        viewCount: Number(item.viewCount ?? item.ViewCount ?? 0),
        totalQuotaConsumptionCount: Number(item.totalQuotaConsumptionCount ?? item.TotalQuotaConsumptionCount ?? 0),
        uniqueReaderCount: Number(item.uniqueReaderCount ?? item.UniqueReaderCount ?? 0),
        favoriteCount: Number(item.favoriteCount ?? item.FavoriteCount ?? 0),
        commentCount: Number(item.commentCount ?? item.CommentCount ?? 0),
        reactionCount: Number(item.reactionCount ?? item.ReactionCount ?? 0),
        latestPartOrder: item.latestPartOrder ?? item.LatestPartOrder ?? null,
        latestPartTitle: item.latestPartTitle ?? item.LatestPartTitle ?? null,
        latestPartCreatedAtUtc: item.latestPartCreatedAtUtc ?? item.LatestPartCreatedAtUtc ?? null,
        pendingPartCount: Number(item.pendingPartCount ?? item.PendingPartCount ?? 0),
      }));
      setContents(items);
      if (items.length === 0) {
        resetContentForm();
        return;
      }

      if (selectedContentId && !items.some((item) => item.id === selectedContentId)) {
        resetContentForm();
      }
    } catch (error) {
      console.error("[admin] içerikler yüklenemedi", error);
      Swal.fire("Hata", "İçerikler alınamadı.", "error");
    } finally {
      setContentLoading(false);
    }
  }, [creatorScopeId, creatorFilterUserId, isAdminUser, selectedContentId]);

  useEffect(() => {
    void loadContents();
  }, [loadContents]);

  const loadParts = async (contentId: string) => {
    setPartLoading(true);
    try {
      const response = await axios.get(`/contentpart/content/${contentId}`);
      const items: ContentPartItem[] = Array.isArray(response.data) ? response.data : [];
      setParts(items);
    } catch (error) {
      console.error("[admin] bölümler yüklenemedi", error);
      Swal.fire("Hata", "İçeriğe ait bölümler alınamadı.", "error");
    } finally {
      setPartLoading(false);
    }
  };

  const loadContentInsights = async (contentId: string) => {
    if (!contentId) {
      setPartRevenueStats(null);
      setPerformanceSeries(null);
      return;
    }

    setInsightLoading(true);
    try {
      const [partRevenue, performance] = await Promise.all([
        getContentPartRevenue(contentId),
        getSingleContentPerformanceStats(contentId, 14),
      ]);

      setPartRevenueStats(partRevenue);
      setPerformanceSeries(
        performance
          ? {
              contentId: performance.contentId,
              title: performance.title,
              data: performance.data,
            }
          : null,
      );
    } catch (error) {
      console.error("[admin] content insights load failed", error);
      setPartRevenueStats(null);
      setPerformanceSeries(null);
    } finally {
      setInsightLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedContentId || !showDetailView) {
      return;
    }

    void loadContentInsights(selectedContentId);
  }, [selectedContentId, showDetailView]);

  const handleSelectContent = (content: ContentItem) => {
    setSelectedContentId(content.id);
    setShowDetailView(true);
    setDetailTab("overview");
    setContentForm({
      id: content.id,
      title: content.title ?? "",
      type: content.type ?? "Webtoon",
      author: content.author ?? "",
      actor: content.actor ?? "",
      category: content.category ?? "",
      description: content.description ?? "",
      releaseDate:content.releaseDate ? content.releaseDate.split("T")[0] : new Date().toISOString().slice(0, 10),
      heroImageUrl: content.heroImageUrl ?? "",
      coverImageUrl: content.coverImageUrl ?? "",
      tileImageUrl: content.tileImageUrl ?? "",
    });
    setPartForm({ ...defaultPartForm, contentId: content.id });
  };

  const resetContentForm = () => {
    setContentForm(defaultContentForm);
    setSelectedContentId("");
    setShowDetailView(false);
    setDetailTab("overview");
    setPartRevenueStats(null);
    setPerformanceSeries(null);
    setParts([]);
  };

  const handleContentFieldChange = (field: keyof typeof contentForm, value: string) => {
    setContentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContentUpload = async (field: UploadKey, files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setUploadingContent((prev) => ({ ...prev, [field]: true }));
    try {
      const fileName = await uploadSingleAsset(files[0], { kind: "image" });
      setContentForm((prev) => ({ ...prev, [field]: fileName }));
    } catch (error: any) {
      console.error("[admin] content upload failed", error);
      Swal.fire("Hata", error?.message ?? "Dosya yüklenemedi.", "error");
    } finally {
      setUploadingContent((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSaveContent = async () => {
    if (!contentForm.id && !canCreateContent) {
      Swal.fire("Yetkisiz islem", "Yeni seri olusturma yetkiniz yok.", "error");
      return;
    }
    if (contentForm.id && !canEditSelectedContent) {
      Swal.fire("Yetkisiz islem", "Bu seriyi duzenleme yetkiniz yok.", "error");
      return;
    }
    if (creatorRestricted && !contentForm.id) {
      Swal.fire("Bilgilendirme", "Yeni seri oluşturmak için Yayinci panelini kullanmalisiniz.", "info");
      return;
    }
    if (creatorRestricted && contentForm.id && !selectedContentOwned) {
      Swal.fire("Yetkisiz işlem", "Bu seriyi düzenleme yetkiniz yok.", "error");
      return;
    }
    if (!contentForm.title.trim()) {
      Swal.fire("Eksik bilgi", "Başlık zorunludur.", "warning");
      return;
    }
    if (!contentForm.heroImageUrl || !contentForm.coverImageUrl || !contentForm.tileImageUrl) {
      Swal.fire("Eksik bilgi", "Tüm görseller yüklenmelidir.", "warning");
      return;
    }

    setSavingContent(true);
    try {
      const payload = {
        title: contentForm.title.trim(),
        type: contentForm.type,
        author: contentForm.author.trim(),
        actor: contentForm.actor.trim() || null,
        heroImageUrl: contentForm.heroImageUrl,
        coverImageUrl: contentForm.coverImageUrl,
        tileImageUrl: contentForm.tileImageUrl,
        category: contentForm.category.trim(),
        releaseDate: new Date(contentForm.releaseDate),
        description: contentForm.description?.trim() || null,
      };

      if (contentForm.id) {
        await axios.put("/content", { ...payload, id: contentForm.id });
        Swal.fire("Başarılı", "İçerik kaydedildi.", "success");
        await loadContents();
        setSelectedContentId(contentForm.id);
      } else {
        const res = await axios.post("/content", payload);
        const newId: string | undefined = (res.data?.id ?? res.data?.Id ?? "").toString() || undefined;
        Swal.fire("Başarılı", "Seri oluşturuldu.", "success");
        await loadContents();
        if (newId) {
          setSelectedContentId(newId);
          setContentForm((prev) => ({ ...prev, id: newId }));
          setDetailTab("overview");
        }
      }
    } catch (error) {
      console.error("[admin] content save failed", error);
      Swal.fire("Hata", "İçerik kaydedilemedi.", "error");
    } finally {
      setSavingContent(false);
    }
  };

  const handleDeleteContent = async () => {
    if (!canDeleteContent) {
      Swal.fire("Yetkisiz islem", "Seri silme yetkiniz yok.", "error");
      return;
    }
    if (!contentForm.id) {
      return;
    }
    if (creatorRestricted && !selectedContentOwned) {
      Swal.fire("Yetkisiz işlem", "Bu seriyi silme yetkiniz yok.", "error");
      return;
    }
    const confirm = await Swal.fire({
      title: "İçerik silinecek",
      text: "Bu işlem geri alınamaz.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sil",
      cancelButtonText: "Vazgeç",
    });
    if (!confirm.isConfirmed) {
      return;
    }

    const secretPrompt = await Swal.fire({
      title: "Silme doğrulaması",
      text: "Lütfen silme şifresini girin.",
      input: "password",
      inputPlaceholder: "Delete secret",
      confirmButtonText: "Onayla",
      cancelButtonText: "Vazgeç",
      showCancelButton: true,
      inputAttributes: {
        autocomplete: "off",
      },
    });

    if (!secretPrompt.isConfirmed || !secretPrompt.value) {
      Swal.fire("İşlem iptal edildi", "Silme şifresi girilmedi.", "info");
      return;
    }

    try {
      await axios.delete(`/content/${contentForm.id}`, {
        headers: {
          "X-Delete-Secret": secretPrompt.value,
          "X-Delete-Secret-Confirm": secretPrompt.value,
        },
      });
      Swal.fire("Silindi", "İçerik kaldırıldı.", "success");
      resetContentForm();
      await loadContents();
    } catch (error) {
      console.error("[admin] content delete failed", error);
      Swal.fire("Hata", "İçerik silinemedi.", "error");
    }
  };

  const handleTransferOwnership = async () => {
    if (!canTransferOwnership) {
      Swal.fire("Yetkisiz islem", "Sahiplik aktarma yetkiniz yok.", "error");
      return;
    }
    if (!isAdminUser) {
      Swal.fire("Yetki yok", "Bu islem yalnizca admin kullanicilar icin aciktir.", "warning");
      return;
    }
    if (!selectedContentId) {
      Swal.fire("Icerik secin", "Once bir seri secmelisiniz.", "warning");
      return;
    }
    if (!ownershipTargetUserId) {
      Swal.fire("Yayinci secin", "Seriyi atamak icin bir kullanici secmelisiniz.", "warning");
      return;
    }
    if (selectedContent?.createdByUserId === ownershipTargetUserId) {
      Swal.fire("Bilgi", "Bu seri zaten secili kullaniciya ait.", "info");
      return;
    }

    setTransferLoading(true);
    const result = await transferContentOwnership(selectedContentId, {
      targetUserId: ownershipTargetUserId,
      note: ownershipNote.trim() || undefined,
    });
    setTransferLoading(false);

    if (!result.success) {
      Swal.fire("Hata", result.message ?? "Seri sahipligi guncellenemedi.", "error");
      return;
    }

    const isNoopTransfer =
      result.newOwnerUserId?.toLowerCase() === selectedContent?.createdByUserId?.toLowerCase() ||
      (result.message?.toLocaleLowerCase("tr-TR").includes("zaten") ?? false);

    if (isNoopTransfer) {
      Swal.fire("Bilgi", result.message ?? "Icerik zaten secili kullaniciya ait.", "info");
    } else {
      Swal.fire("Basarili", result.message ?? "Seri yeni creator hesabina aktarildi.", "success");
    }

    setOwnershipNote("");
    await loadContents();
    await loadParts(selectedContentId);
  };

  const handlePartFieldChange = (field: keyof typeof partForm, value: string | number | boolean | string[]) => {
    setPartForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePartUpload = async (field: PartUploadKey, files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const key = field === "mediaBanner" ? "banner" : "doc";
    setUploadingPart((prev) => ({ ...prev, [key]: true }));
    try {
      const fileName = await uploadSingleAsset(files[0], {
        kind: field === "mediaBanner" ? "image" : "docx",
      });
      setPartForm((prev) => ({ ...prev, [field]: fileName }));
    } catch (error: any) {
      console.error("[admin] part upload failed", error);
      Swal.fire("Hata", error?.message ?? "Dosya yüklenemedi.", "error");
    } finally {
      setUploadingPart((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handlePartImagesUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setUploadingPart((prev) => ({ ...prev, images: true }));
    try {
      const uploaded = await uploadMultipleAssets(Array.from(files), { kind: "image" });
      setPartForm((prev) => ({ ...prev, imageList: [...prev.imageList, ...uploaded] }));
    } catch (error: any) {
      console.error("[admin] gallery upload failed", error);
      Swal.fire("Hata", error?.message ?? "Görseller yüklenemedi.", "error");
    } finally {
      setUploadingPart((prev) => ({ ...prev, images: false }));
    }
  };

  const handleEditPart = (part: ContentPartItem) => {
    setPartForm({
      id: part.id,
      contentId: selectedContentId,
      title: part.title ?? "",
      description: part.description ?? "",
      order: part.order ?? 1,
      price:part.price != null ? String(part.price) : "",
      isFreePreview: part.isFreePreview ?? false,
      mediaBanner: part.mediaBanner ?? "",
      mediaUrl: part.mediaUrl ?? "",
      imageList: part.imageList ? [...part.imageList] : [],
    });
  };

  const handleRemovePartImage = (index: number) => {
    setPartForm((prev) => ({ ...prev, imageList: prev.imageList.filter((_, i) => i !== index) }));
  };

  const handleSavePart = async () => {
    if (!canEditSelectedContent) {
      Swal.fire("Yetkisiz islem", "Bu seri icin bolum duzenleme yetkiniz yok.", "error");
      return;
    }
    if (!selectedContentId) {
      Swal.fire("Uyarı", "Önce içerik seçmelisiniz.", "warning");
      return;
    }
    if (creatorRestricted && !selectedContentOwned) {
      Swal.fire("Yetkisiz işlem", "Bu seriye ait bölümleri düzenleyemezsiniz.", "error");
      return;
    }
    if (!partForm.title.trim()) {
      Swal.fire("Eksik bilgi", "Bölüm başlığı zorunludur.", "warning");
      return;
    }

    setSavingPart(true);
    try {
      const payload = {
        contentId: selectedContentId,
        title: partForm.title.trim(),
        description: partForm.description?.trim() ?? "",
        order: Number(partForm.order) > 0 ? Number(partForm.order) : 1,
        price:partForm.price ? Number(partForm.price) : null,
        isFreePreview: Boolean(partForm.isFreePreview),
        mediaBanner: partForm.mediaBanner || null,
        mediaUrl: partForm.mediaUrl || null,
        imageList: partForm.imageList.length > 0 ? partForm.imageList : null,
      };

      if (partForm.id) {
        await axios.put("/contentpart", { ...payload, id: partForm.id });
      } else {
        await axios.post("/contentpart", payload);
      }

      Swal.fire("Başarılı", "Bölüm kaydedildi.", "success");
      await loadParts(selectedContentId);
      setPartForm({ ...defaultPartForm, contentId: selectedContentId });
    } catch (error) {
      console.error("[admin] part save failed", error);
      Swal.fire("Hata", "Bölüm kaydedilemedi.", "error");
    } finally {
      setSavingPart(false);
    }
  };

  const handleDeletePart = async (part: ContentPartItem) => {
    if (!canEditSelectedContent) {
      Swal.fire("Yetkisiz islem", "Bu seri icin bolum silme yetkiniz yok.", "error");
      return;
    }
    if (!part.id) {
      return;
    }
    if (creatorRestricted && !selectedContentOwned) {
      Swal.fire("Yetkisiz işlem", "Bu seriye ait bölümleri silemezsiniz.", "error");
      return;
    }
    const confirm = await Swal.fire({
      title: "Bölüm silinsin mi?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sil",
      cancelButtonText: "Vazgeç",
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      await axios.delete(`/contentpart/${part.id}`);
      Swal.fire("Silindi", "Bölüm kaldırıldı.", "success");
      await loadParts(selectedContentId);
    } catch (error) {
      console.error("[admin] part delete failed", error);
      Swal.fire("Hata", "Bölüm silinemedi.", "error");
    }
  };

  const toggleQueueSelection = (contentId: string, checked: boolean) => {
    setSelectedQueueIds((prev) => {
      if (checked) {
        return prev.includes(contentId) ? prev : [...prev, contentId];
      }
      return prev.filter((id) => id !== contentId);
    });
  };

  const handleBulkModeration = async (action: "approve" | "reject") => {
    if (!canPublishContent) {
      Swal.fire("Yetkisiz islem", "Toplu moderasyon yetkiniz yok.", "error");
      return;
    }
    if (selectedQueueIds.length === 0) {
      Swal.fire("Secim gerekli", "Islem icin en az bir seri secmelisiniz.", "warning");
      return;
    }

    let reason: string | undefined;
    if (action === "reject") {
      const prompt = await Swal.fire({
        title: "Toplu red nedeni",
        input: "text",
        inputPlaceholder: "Red nedeni (opsiyonel)",
        showCancelButton: true,
        confirmButtonText: "Devam et",
        cancelButtonText: "Vazgec",
      });
      if (!prompt.isConfirmed) {
        return;
      }
      reason = String(prompt.value ?? "").trim() || undefined;
    } else {
      const confirm = await Swal.fire({
        title: "Toplu onay",
        text: `${selectedQueueIds.length} seri onaylanacak.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Onayla",
        cancelButtonText: "Vazgec",
      });
      if (!confirm.isConfirmed) {
        return;
      }
    }

    setBulkModerationLoading(true);
    let successCount = 0;
    for (const contentId of selectedQueueIds) {
      const ok =
        action === "approve"
          ? await approveContentSubmission(contentId)
          : await rejectContentSubmission(contentId, reason);
      if (ok) {
        successCount += 1;
      }
    }
    setBulkModerationLoading(false);

    await loadContents();
    setSelectedQueueIds([]);

    if (successCount === 0) {
      Swal.fire("Hata", "Toplu moderasyon islemi basarisiz oldu.", "error");
      return;
    }

    const total = selectedQueueIds.length;
    Swal.fire(
      "Tamamlandi",
      `${successCount}/${total} seri için ${action === "approve" ? "onay" : "red"} islemi uygulandi.`,
      successCount === total ? "success" : "warning",
    );
  };

  if (!showDetailView) {
    return (
      <div className="space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Seri kataloğu</h2>
              <p className="text-xs text-slate-500">
                Önce seri arayıp filtrele, kart üstünden temel metrikleri gör, ardından detay ekranına geç.
              </p>
            </div>
            {!creatorRestricted && canCreateContent && (
              <button
                type="button"
                onClick={() => {
                  resetContentForm();
                  setShowDetailView(true);
                  setDetailTab("edit");
                }}
                className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                + Yeni Seri
              </button>
            )}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <input
              type="text"
              placeholder="Başlık, yazar veya kategori ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm xl:col-span-2"
            />

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="all">Tüm türler</option>
              {CONTENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ContentStatusFilter)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="all">Tüm durumlar</option>
              <option value="approved">Yayında</option>
              <option value="pending">Onay bekliyor</option>
            </select>

            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as ContentSortMode)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="recent">Son güncelleme</option>
              <option value="views">En çok görüntülenme</option>
              <option value="favorites">En çok favori</option>
              <option value="quota">En çok okuma tüketimi</option>
            </select>
          </div>

          {isAdminUser && !creatorRestricted && (
            <label className="mt-3 block text-xs font-semibold text-slate-600">
              Yayinci filtresi
              <select
                value={creatorFilterUserId}
                onChange={(e) => setCreatorFilterUserId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Tüm creatorlar</option>
                {creatorOwnerOptions.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name} ({owner.email})
                    </option>
                  ))}
              </select>
            </label>
          )}
        </section>

        <section className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
          <StatChip label="Filtrelenen seri" value={formatCompact(catalogSummary.total)} />
          <StatChip label="Yayinda" value={formatCompact(catalogSummary.approved)} />
          <StatChip label="Onay bekleyen" value={formatCompact(catalogSummary.pending)} />
          <StatChip label="Yayinci serisi" value={formatCompact(catalogSummary.creatorOwned)} />
          <StatChip label="Platform serisi" value={formatCompact(catalogSummary.platformOwned)} />
          <StatChip label="Bekleyen bolum" value={formatCompact(catalogSummary.pendingParts)} />
        </section>

        <section className="grid gap-3 lg:grid-cols-2">
          <article className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700">Onay kuyrugu</h3>
              <span className="text-[11px] text-amber-700">{formatCompact(catalogSummary.pending)} seri</span>
            </div>
            {canPublishContent && moderationQueues.pendingContents.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleBulkModeration("approve")}
                  disabled={bulkModerationLoading || selectedQueueIds.length === 0}
                  className="rounded border border-emerald-300 bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {bulkModerationLoading ? "Isleniyor..." : "Toplu onay"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleBulkModeration("reject")}
                  disabled={bulkModerationLoading || selectedQueueIds.length === 0}
                  className="rounded border border-rose-300 bg-rose-100 px-2 py-1 text-[11px] font-semibold text-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {bulkModerationLoading ? "Isleniyor..." : "Toplu red"}
                </button>
                <span className="text-[11px] text-amber-700">Secili: {formatCompact(selectedQueueIds.length)}</span>
              </div>
            )}
            {moderationQueues.pendingContents.length === 0 ? (
              <p className="text-xs text-amber-700">Onay bekleyen seri yok.</p>
            ) : (
              <div className="space-y-1">
                {moderationQueues.pendingContents.map((content) => (
                  <div
                    key={`pending-${content.id}`}
                    className="flex items-center gap-2 rounded border border-amber-200 bg-white px-2 py-1"
                  >
                    {canPublishContent && (
                      <input
                        type="checkbox"
                        checked={selectedQueueIds.includes(content.id)}
                        onChange={(event) => toggleQueueSelection(content.id, event.target.checked)}
                        className="h-3.5 w-3.5 accent-amber-600"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => handleSelectContent(content)}
                      className="flex w-full items-center justify-between text-left text-xs hover:text-amber-800"
                    >
                      <span className="truncate text-slate-800">{content.title}</span>
                      <span className="shrink-0 text-[11px] text-amber-700">Detay</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </article>

          <article className="rounded-xl border border-indigo-200 bg-indigo-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Bolum kuyrugu</h3>
              <span className="text-[11px] text-indigo-700">{formatCompact(catalogSummary.pendingParts)} bekleyen</span>
            </div>
            {moderationQueues.pendingPartsContents.length === 0 ? (
              <p className="text-xs text-indigo-700">Bekleyen bolum yok.</p>
            ) : (
              <div className="space-y-1">
                {moderationQueues.pendingPartsContents.map((content) => (
                  <button
                    key={`parts-${content.id}`}
                    type="button"
                    onClick={() => handleSelectContent(content)}
                    className="flex w-full items-center justify-between rounded border border-indigo-200 bg-white px-2 py-1 text-left text-xs hover:bg-indigo-100"
                  >
                    <span className="truncate text-slate-800">{content.title}</span>
                    <span className="shrink-0 text-[11px] text-indigo-700">{formatCompact(content.pendingPartCount ?? 0)} bolum</span>
                  </button>
                ))}
              </div>
            )}
          </article>
        </section>

        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {contentLoading && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
              İçerikler yükleniyor...
            </div>
          )}

          {!contentLoading && filteredContents.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
              Filtrelere uygun seri bulunamadı.
            </div>
          )}

          {!contentLoading &&
            filteredContents.map((content) => {
              const cover =
                resolveAssetUrl(content.coverImageUrl) ??                 resolveAssetUrl(content.heroImageUrl) ??                 resolveAssetUrl(content.tileImageUrl);
              const creatorOwned = isCreatorOwnedContent(content, ownerMap);
              const ownerName =
                content.createdByUserId && ownerMap.get(content.createdByUserId)
                  ? ownerMap.get(content.createdByUserId)?.name
                  : "Atanmamış";

              return (
                <article key={content.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  {cover ? (
                    <img src={cover} alt={content.title} className="h-40 w-full rounded-t-2xl object-cover" />
                  ) : (
                    <div className="flex h-40 items-center justify-center rounded-t-2xl bg-slate-100 text-xs text-slate-500">
                      Kapak görseli yok
                    </div>
                  )}

                  <div className="space-y-3 p-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="line-clamp-1 text-sm font-semibold text-slate-900">{content.title}</div>
                        {creatorOwned && (
                          <span className="inline-flex shrink-0 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                            Yayinci
                          </span>
                        )}
                        {!creatorOwned && (
                          <span className="inline-flex shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            Merkez
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{content.type} · {content.category || "Kategori yok"}</div>
                      <div className="text-[11px] text-slate-500">
                        Durum: {content.isApproved ? "Yayında" : "Onay bekliyor"} · Sahip: {ownerName}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-700">
                      <div className="rounded-lg bg-slate-50 px-2 py-1">
                        <div className="text-slate-500">Görüntülenme</div>
                        <div className="font-semibold">{formatCompact(content.viewCount ?? 0)}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 px-2 py-1">
                        <div className="text-slate-500">Okuma tüketimi</div>
                        <div className="font-semibold">{formatCompact(content.totalQuotaConsumptionCount ?? 0)}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 px-2 py-1">
                        <div className="text-slate-500">Benzersiz okuyucu</div>
                        <div className="font-semibold">{formatCompact(content.uniqueReaderCount ?? 0)}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 px-2 py-1">
                        <div className="text-slate-500">Favori</div>
                        <div className="font-semibold">{formatCompact(content.favoriteCount ?? 0)}</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleSelectContent(content)}
                      className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
                    >
                      Seri detayını aç
                    </button>
                  </div>
                </article>
              );
            })}
        </section>
      </div>
    );
  }

  if (!selectedContent) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
          <button
            type="button"
            onClick={() => setShowDetailView(false)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            ← Geri dön
          </button>
          <h2 className="text-lg font-semibold text-slate-900">Yeni Seri Oluştur</h2>
        </div>
        <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-base font-semibold text-slate-800">Seri Bilgileri</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs font-semibold text-slate-600">
              Başlık
              <input className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" value={contentForm.title} onChange={(e) => handleContentFieldChange("title", e.target.value)} />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Tür
              <select className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" value={contentForm.type} onChange={(e) => handleContentFieldChange("type", e.target.value)}>
                {CONTENT_TYPES.map((type) => <option key={type}>{type}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Yazar
              <input className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" value={contentForm.author} onChange={(e) => handleContentFieldChange("author", e.target.value)} />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Çizer / Aktör
              <input className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" value={contentForm.actor} onChange={(e) => handleContentFieldChange("actor", e.target.value)} />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Kategori
              <input className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" value={contentForm.category} onChange={(e) => handleContentFieldChange("category", e.target.value)} />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Yayın Tarihi
              <input type="date" className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" value={contentForm.releaseDate} onChange={(e) => handleContentFieldChange("releaseDate", e.target.value)} />
            </label>
          </div>
          <label className="text-xs font-semibold text-slate-600">
            Açıklama
            <textarea className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm" rows={3} value={contentForm.description} onChange={(e) => handleContentFieldChange("description", e.target.value)} />
          </label>
          <div className="grid gap-4 md:grid-cols-3">
            {(["heroImageUrl", "coverImageUrl", "tileImageUrl"] as UploadKey[]).map((field) => {
              const labelText = field === "heroImageUrl" ? "Hero Görseli" : field === "coverImageUrl" ? "Kapak Görseli" : "Keşfet Kapağı";
              return (
                <label key={field} className="text-xs font-semibold text-slate-600">
                  {labelText}
                  <input type="file" accept=".webp,.jpg,.jpeg,.png" className="mt-2 w-full text-xs" disabled={uploadingContent[field]} onChange={(e) => { void handleContentUpload(field, e.target.files); e.currentTarget.value = ""; }} />
                  <div className="mt-1 text-[11px] text-slate-500">{uploadingContent[field] ? "Yükleniyor..." : contentForm[field] ? resolveAssetUrl(contentForm[field]) : "Dosya seçilmedi."}</div>
                </label>
              );
            })}
          </div>
          <button
            onClick={handleSaveContent}
            disabled={savingContent || !canCreateContent}
            className="rounded bg-indigo-600 px-6 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-indigo-700"
          >
            {savingContent ? "Kaydediliyor..." : "Seri Oluştur"}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{selectedContent.title}</h2>
            <p className="text-xs text-slate-500">
              {selectedContent.type} · {selectedContent.category || "Kategori yok"} · {selectedContent.isApproved ? "Yayında" : "Onay bekliyor"}
            </p>
            <p className="text-[11px] text-slate-500">
              Sahip:{" "}
              {selectedContent.createdByUserId
                ? ownerMap.get(selectedContent.createdByUserId)?.name ?? "Bilinmiyor"
                : "Atanmamış"}
            </p>
            {isCreatorOwnedContent(selectedContent, ownerMap) && (
              <p className="mt-1 inline-flex rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                Yayinci serisi
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowDetailView(false)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Listeye geri dön
            </button>
            <button
              type="button"
              onClick={() => setDetailTab("overview")}
              className={`rounded-lg px-3 py-2 text-xs font-semibold ${
                detailTab === "overview" ? "bg-indigo-600 text-white" : "border border-slate-300 bg-white text-slate-700"
              }`}
            >
              Detay
            </button>
            <button
              type="button"
              onClick={() => setDetailTab("edit")}
              className={`rounded-lg px-3 py-2 text-xs font-semibold ${
                detailTab === "edit" ? "bg-indigo-600 text-white" : "border border-slate-300 bg-white text-slate-700"
              }`}
            >
              Düzenle
            </button>
            <button
              type="button"
              onClick={() => setDetailTab("parts")}
              className={`rounded-lg px-3 py-2 text-xs font-semibold ${
                detailTab === "parts" ? "bg-indigo-600 text-white" : "border border-slate-300 bg-white text-slate-700"
              }`}
            >
              Bölümler
            </button>
          </div>
        </div>

        {detailTab === "overview" && (
          <div className="space-y-4">
            <div className="grid gap-2 md:grid-cols-3 xl:grid-cols-6">
              <StatChip label="Görüntülenme" value={formatCompact(selectedContent.viewCount ?? 0)} />
              <StatChip label="Okuma tüketimi" value={formatCompact(selectedContent.totalQuotaConsumptionCount ?? 0)} />
              <StatChip label="Benzersiz okuyucu" value={formatCompact(selectedContent.uniqueReaderCount ?? 0)} />
              <StatChip label="Favori" value={formatCompact(selectedContent.favoriteCount ?? 0)} />
              <StatChip label="Yorum" value={formatCompact(selectedContent.commentCount ?? 0)} />
              <StatChip label="Bekleyen bölüm" value={formatCompact(selectedContent.pendingPartCount ?? 0)} />
            </div>

            {insightLoading && <div className="text-xs text-slate-500">Seri istatistikleri yukleniyor...</div>}

            {!insightLoading && (
              <div className="grid gap-3 lg:grid-cols-2">
                <article className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Okuma dağılımı</div>
                  <div className="mt-2 space-y-2 text-xs">
                    <RatioRow
                      label="Ücretsiz"
                      value={partRevenueStats?.summary?.freeReads ?? 0}
                      total={
                        (partRevenueStats?.summary?.freeReads ?? 0) +
                        (partRevenueStats?.summary?.premiumReads ?? 0) +
                        (partRevenueStats?.summary?.singleUnlockReads ?? 0)
                      }
                    />
                    <RatioRow
                      label="Premium paket"
                      value={partRevenueStats?.summary?.premiumReads ?? 0}
                      total={
                        (partRevenueStats?.summary?.freeReads ?? 0) +
                        (partRevenueStats?.summary?.premiumReads ?? 0) +
                        (partRevenueStats?.summary?.singleUnlockReads ?? 0)
                      }
                    />
                    <RatioRow
                      label="Tekli açma"
                      value={partRevenueStats?.summary?.singleUnlockReads ?? 0}
                      total={
                        (partRevenueStats?.summary?.freeReads ?? 0) +
                        (partRevenueStats?.summary?.premiumReads ?? 0) +
                        (partRevenueStats?.summary?.singleUnlockReads ?? 0)
                      }
                    />
                  </div>
                </article>

                <article className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gelir özeti (Z)</div>
                  <div className="mt-2 grid gap-2 md:grid-cols-3">
                    <StatChip label="Premium gelir" value={formatCompact(partRevenueStats?.summary?.premiumRevenue ?? 0)} />
                    <StatChip label="Tekli gelir" value={formatCompact(partRevenueStats?.summary?.singleUnlockRevenue ?? 0)} />
                    <StatChip label="Toplam gelir" value={formatCompact(partRevenueStats?.summary?.totalRevenue ?? 0)} />
                  </div>
                </article>
              </div>
            )}

            <article className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">14 günlük okuma grafiği</div>
              {performanceSeries && performanceSeries.data.length > 0 ? (
                <MiniReadChart points={performanceSeries.data.map((item) => ({ date: item.date, readCount: item.readCount }))} />
              ) : (
                <div className="text-xs text-slate-500">Bu seri icin 14 gunluk trend verisi henuz yok.</div>
              )}
            </article>

            <article className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Bölüm gelir kırılımı</div>
              {partRevenueStats?.items?.length ? (
                <div className="space-y-2">
                  {partRevenueStats.items.slice(0, 8).map((item) => (
                    <RevenueBar
                      key={item.contentPartId}
                      label={item.title}
                      value={Number(item.totalRevenue ?? 0)}
                      max={Math.max(...partRevenueStats.items.map((entry) => Number(entry.totalRevenue ?? 0)), 1)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500">Bölüm bazlı gelir verisi bulunamadı.</div>
              )}
            </article>
          </div>
        )}
      </section>

      {detailTab === "edit" && (
      <div className="grid gap-4 lg:grid-cols-[320px,1fr]">
        <aside className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">İçerikler</h2>
            <button
              onClick={() => {
                if (creatorRestricted) {
                  Swal.fire("Bilgilendirme", "Yeni seri olusturmak icin Yayinci panelini kullanin.", "info");
                  return;
                }
                resetContentForm();
              }}
              className={`text-xs ${creatorRestricted || !canCreateContent ? "text-slate-400 cursor-not-allowed" : "text-blue-600 hover:underline"}`}
              disabled={creatorRestricted || !canCreateContent}
            >
              Yeni
            </button>
          </div>
          {creatorRestricted && (
            <p className="mb-3 text-[11px] text-slate-500">
              Bu panelde yalnizca mevcut serilerini goruntuleyip duzenleyebilirsin. Yeni seri gondermek icin Yayinci panelini kullan.
            </p>
          )}
          {isAdminUser && !creatorRestricted && (
            <label className="mb-3 block text-[11px] font-semibold text-slate-600">
              Yayinci filtresi
              <select
                value={creatorFilterUserId}
                onChange={(e) => setCreatorFilterUserId(e.target.value)}
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs"
              >
                <option value="">Tüm creatorlar</option>
                {creatorOwnerOptions.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name} ({owner.email})
                    </option>
                  ))}
              </select>
            </label>
          )}
          <input
            type="text"
            placeholder="Ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3 w-full rounded border border-slate-200 px-2 py-1 text-sm"
          />
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: "400px" }}>
            {contentLoading && <div className="text-xs text-slate-500">Yükleniyor...</div>}
            {!contentLoading && filteredContents.length === 0 && (
              <div className="text-xs text-slate-500">İçerik bulunamadı.</div>
            )}
            {filteredContents.map((content) => {
              const active = selectedContentId === content.id;
              const creatorOwned = isCreatorOwnedContent(content, ownerMap);
              return (
                <button
                  key={content.id}
                  onClick={() => handleSelectContent(content)}
                  className={`w-full rounded border px-3 py-2 text-left text-sm ${
                    active ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-slate-800">{content.title}</div>
                    {creatorOwned && (
                      <span className="inline-flex shrink-0 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                        Yayinci
                      </span>
                    )}
                    {!creatorOwned && (
                      <span className="inline-flex shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        Merkez
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{content.category}</div>
                  {isAdminUser && content.createdByUserId && (
                    <div className="text-[11px] text-slate-500">
                      Sahip: {ownerMap.get(content.createdByUserId)?.name ?? "Bilinmiyor"}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-base font-semibold text-slate-800">{contentForm.id ? "İçerik Düzenle" : "İçerik Oluştur"}</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs font-semibold text-slate-600">
              Başlık
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
                value={contentForm.title}
                onChange={(e) => handleContentFieldChange("title", e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Tür
              <select
                className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
                value={contentForm.type}
                onChange={(e) => handleContentFieldChange("type", e.target.value)}
              >
                {CONTENT_TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Yazar
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
                value={contentForm.author}
                onChange={(e) => handleContentFieldChange("author", e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Çizer / Aktör
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
                value={contentForm.actor}
                onChange={(e) => handleContentFieldChange("actor", e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Kategori
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
                value={contentForm.category}
                onChange={(e) => handleContentFieldChange("category", e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Yayın Tarihi
              <input
                type="date"
                className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
                value={contentForm.releaseDate}
                onChange={(e) => handleContentFieldChange("releaseDate", e.target.value)}
              />
            </label>
          </div>

          <label className="text-xs font-semibold text-slate-600">
            Açıklama
            <textarea
              className="mt-1 w-full rounded border border-slate-200 px-2 py-2 text-sm"
              rows={3}
              value={contentForm.description}
              onChange={(e) => handleContentFieldChange("description", e.target.value)}
            />
          </label>

          <div className="grid gap-4 md:grid-cols-3">
            {(["heroImageUrl", "coverImageUrl", "tileImageUrl"] as UploadKey[]).map((field) => {
              const labelText =
                field === "heroImageUrl"
                  ? "Hero Görseli"
                  : field === "coverImageUrl"
                  ? "Kapak Görseli"
                  : "Keşfet Kapağı";
              const hintText =
                field === "heroImageUrl"
                  ? "Anasayfa hero sliderı ve içerik detay hero bölümünde görünür."
                  : field === "coverImageUrl"
                  ? "İçerik kartları ve öne çıkan listelerde dikey kapak olarak kullanılır."
                  : "Keşfet/grid sayfalarındaki kare kartlarda gösterilir.";
              return (
                <label key={field} className="text-xs font-semibold text-slate-600">
                  {labelText}
                  <input
                    type="file"
                    accept=".webp,.jpg,.jpeg,.png"
                    className="mt-2 w-full text-xs"
                    disabled={uploadingContent[field]}
                    onChange={(e) => {
                      void handleContentUpload(field, e.target.files);
                      e.currentTarget.value = "";
                    }}
                  />
                  <div className="mt-1 text-[11px] text-slate-500">
                    {uploadingContent[field]
                      ? "Yükleniyor..."
                      : contentForm[field]
                      ? resolveAssetUrl(contentForm[field])
                      : "Dosya seçilmedi."}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">{hintText}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Format: PNG/JPG/JPEG/WEBP · Maksimum: 10 MB</p>
                </label>
              );
            })}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSaveContent}
              disabled={savingContent || (creatorRestricted && !contentForm.id) || (!contentForm.id && !canCreateContent) || (Boolean(contentForm.id) && !canEditSelectedContent)}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {savingContent ? "Kaydediliyor..." : contentForm.id ? "Güncelle" : "Oluştur"}
            </button>
            {contentForm.id && canDeleteContent && (
              <button onClick={handleDeleteContent} className="rounded border border-red-500 px-4 py-2 text-sm text-red-500">
                Sil
              </button>
            )}
          </div>
          {creatorRestricted && !contentForm.id && (
            <p className="text-[11px] text-slate-500">
              Yeni seri olusturma yetkisi Yayinci paneli uzerinden kullanilabilir.
            </p>
          )}

          {isAdminUser && canTransferOwnership && contentForm.id && (
            <section className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <h3 className="text-sm font-semibold text-slate-800">Yayinci atamasi</h3>
              <p className="text-xs text-slate-500">
                Seriyi farkli bir kullaniciya aktarabilirsin. Sistem hedef kullaniciya yayinci yetkisini otomatik verir.
              </p>
              <div className="mt-3 space-y-3">
                {/* Arama + not + aktar */}
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={ownerSearchTerm}
                      onChange={(e) => setOwnerSearchTerm(e.target.value)}
                      placeholder="Ad veya e-posta ile ara..."
                      className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    />
                    <span className="pointer-events-none absolute left-2.5 top-2.5 text-slate-400 text-xs">🔍</span>
                  </div>
                  <input
                    type="text"
                    value={ownershipNote}
                    onChange={(e) => setOwnershipNote(e.target.value)}
                    placeholder="Not (opsiyonel)"
                    className="flex-1 min-w-[140px] rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                  <button
                    type="button"
                    onClick={() => void handleTransferOwnership()}
                    disabled={transferLoading || !ownershipTargetUserId}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {transferLoading ? "Aktarılıyor..." : "Yayıncıya Aktar"}
                  </button>
                </div>

                {/* Arama durumu */}
                {ownerSearchTerm.trim().length >= 2 && (
                  <p className="text-[11px] text-slate-500">
                    {ownerSearchLoading ? "Aranıyor..." : `${transferOwnerOptions.length} kullanıcı bulundu`}
                  </p>
                )}

                {/* Seçili kullanıcı banner */}
                {ownershipTargetUserId && (() => {
                  const sel = transferOwnerOptions.find((o) => o.id === ownershipTargetUserId)
                    ?? ownerOptions.find((o) => o.id === ownershipTargetUserId);
                  if (!sel) return null;
                  return (
                    <div className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white uppercase">
                        {sel.name.charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-800">{sel.name}</p>
                        <p className="truncate text-xs text-slate-500">{sel.email}</p>
                      </div>
                      <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        sel.role?.toLowerCase().includes("admin") ? "bg-red-100 text-red-700" :
                        sel.role?.toLowerCase().includes("creator") || sel.role?.toLowerCase().includes("yayıncı") ? "bg-emerald-100 text-emerald-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {sel.role || "Üye"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setOwnershipTargetUserId("")}
                        className="flex-shrink-0 text-slate-400 hover:text-red-500"
                        title="Seçimi temizle"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })()}

                {/* Kullanıcı kart listesi */}
                {ownerSearchTerm.trim().length >= 2 && !ownerSearchLoading && transferOwnerOptions.length > 0 && (
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                    {transferOwnerOptions.map((owner) => {
                      const isSelected = owner.id === ownershipTargetUserId;
                      const roleLower = owner.role?.toLowerCase() ?? "";
                      const roleColor = roleLower.includes("admin")
                        ? "bg-red-100 text-red-700"
                        : roleLower.includes("creator") || roleLower.includes("yayıncı")
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600";
                      return (
                        <button
                          key={owner.id}
                          type="button"
                          onClick={() => setOwnershipTargetUserId(owner.id)}
                          className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition ${
                            isSelected
                              ? "bg-indigo-50 ring-1 ring-indigo-300"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600 uppercase">
                            {owner.name.charAt(0)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold text-slate-800">{owner.name}</p>
                            <p className="truncate text-xs text-slate-500">{owner.email}</p>
                          </div>
                          <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${roleColor}`}>
                            {owner.role || "Üye"}
                          </span>
                          {isSelected && (
                            <span className="flex-shrink-0 text-indigo-600 text-xs font-bold">✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {ownerSearchTerm.trim().length >= 2 && !ownerSearchLoading && transferOwnerOptions.length === 0 && (
                  <p className="text-xs text-slate-400">Kullanıcı bulunamadı.</p>
                )}
              </div>
            </section>
          )}
        </section>
      </div>
      )}

      {detailTab === "parts" && (
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Bölümler</h2>
            {!selectedContentId && <p className="text-xs text-slate-500">Önce bir içerik seçin.</p>}
            {creatorRestricted && selectedContentId && !selectedContentOwned && (
              <p className="text-xs text-red-500">Bu seriyi yönetme yetkin yok.</p>
            )}
          </div>
          {selectedContentId && (
            <button
              onClick={() => setPartForm({ ...defaultPartForm, contentId: selectedContentId })}
              disabled={!canEditSelectedContent}
              className={`text-xs ${canEditSelectedContent ? "text-blue-600 hover:underline" : "text-slate-400 cursor-not-allowed"}`}
            >
              Yeni Bölüm
            </button>
          )}
        </div>

        {selectedContentId && (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              {partLoading && <div className="text-xs text-slate-500">Yükleniyor...</div>}
              {!partLoading && parts.length === 0 && (
                <div className="text-xs text-slate-500">Bölüm bulunmuyor. Yeni bölüm oluşturabilirsiniz.</div>
              )}
              {!partLoading &&
                parts.map((part) => (
                  <div
                    key={part.id}
                    className="rounded border border-slate-200 p-3 text-sm hover:border-blue-300"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-800">{part.title}</div>
                        <div className="text-xs text-slate-500">Sira: {part.order ?? "-"} · Fiyat: {part.price ?? 0} Z · Okunma: {formatCompact(part.viewCount ?? 0)}</div>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <button
                          className={canEditSelectedContent ? "text-blue-600 hover:underline" : "text-slate-400 cursor-not-allowed"}
                          onClick={() => handleEditPart(part)}
                          disabled={!canEditSelectedContent}
                        >
                          Düzenle
                        </button>
                        <button
                          className={canEditSelectedContent ? "text-red-600 hover:underline" : "text-slate-400 cursor-not-allowed"}
                          onClick={() => void handleDeletePart(part)}
                          disabled={!canEditSelectedContent}
                        >
                          Sil
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSavePart();
              }}
              className="space-y-3 rounded border border-slate-200 p-3 text-sm"
            >
              <label className="text-xs font-semibold text-slate-600">
                Bölüm Başlığı
                <input
                  className="mt-1 w-full rounded border border-slate-200 px-2 py-2"
                  value={partForm.title}
                  onChange={(e) => handlePartFieldChange("title", e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Açıklama
                <textarea
                  className="mt-1 w-full rounded border border-slate-200 px-2 py-2"
                  rows={3}
                  value={partForm.description}
                  onChange={(e) => handlePartFieldChange("description", e.target.value)}
                />
              </label>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold text-slate-600">
                  Sıra
                  <input
                    type="number"
                    min={1}
                    className="mt-1 w-full rounded border border-slate-200 px-2 py-1"
                    value={partForm.order}
                    onChange={(e) => handlePartFieldChange("order", Number(e.target.value))}
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Fiyat (Z)
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full rounded border border-slate-200 px-2 py-1"
                    value={partForm.price}
                    onChange={(e) => handlePartFieldChange("price", e.target.value)}
                    disabled={partForm.isFreePreview}
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2 pt-5">
                  <input
                    type="checkbox"
                    checked={partForm.isFreePreview}
                    onChange={(e) => handlePartFieldChange("isFreePreview", e.target.checked)}
                  />
                  Ücretsiz önizleme
                </label>
              </div>

              <label className="text-xs font-semibold text-slate-600">
                Banner Görseli
                <input
                  type="file"
                  accept=".webp,.jpg,.jpeg,.png"
                  className="mt-1 text-xs"
                  disabled={uploadingPart.banner}
                  onChange={(e) => {
                    void handlePartUpload("mediaBanner", e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
                <div className="mt-1 text-[11px] text-slate-500">
                  {uploadingPart.banner
                    ? "Yükleniyor..."
                    : partForm.mediaBanner
                    ? resolveAssetUrl(partForm.mediaBanner)
                    : "Dosya seçilmedi."}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Format: PNG/JPG/JPEG/WEBP · Maksimum: 10 MB</p>
              </label>

              <label className="text-xs font-semibold text-slate-600">
                DOCX Dosyası
                <input
                  type="file"
                  accept=".docx"
                  className="mt-1 text-xs"
                  disabled={uploadingPart.doc}
                  onChange={(e) => {
                    void handlePartUpload("mediaUrl", e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
                <div className="mt-1 text-[11px] text-slate-500">
                  {uploadingPart.doc ? "Yükleniyor..." : partForm.mediaUrl ? partForm.mediaUrl : "Dosya seçilmedi."}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Format: DOCX · Maksimum: 10 MB</p>
              </label>

              <label className="text-xs font-semibold text-slate-600">
                Bölüm Görselleri
                <input
                  type="file"
                  accept=".webp,.jpg,.jpeg,.png"
                  multiple
                  className="mt-1 text-xs"
                  disabled={uploadingPart.images}
                  onChange={(e) => {
                    void handlePartImagesUpload(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
                <div className="mt-2 space-y-1">
                  {partForm.imageList.map((img, index) => (
                    <div key={`${img}-${index}`} className="flex items-center justify-between rounded border border-slate-200 px-2 py-1 text-[11px]">
                      <span className="truncate">{img}</span>
                      <button type="button" onClick={() => handleRemovePartImage(index)} className="text-red-500">
                        Sil
                      </button>
                    </div>
                  ))}
                  {partForm.imageList.length === 0 && <div className="text-[11px] text-slate-500">Görsel eklenmedi.</div>}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Format: PNG/JPG/JPEG/WEBP · Maksimum: 10 MB (çoklu yükleme)</p>
              </label>

              <button
                type="submit"
                disabled={savingPart || !canEditSelectedContent}
                className="rounded bg-emerald-600 px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                {savingPart ? "Kaydediliyor..." : partForm.id ? "Bölümü Güncelle" : "Bölüm Oluştur"}
              </button>
            </form>
          </div>
        )}
      </section>
      )}
    </div>
  );
}

